import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SupplyLandInactivityModal from "@SupplyLand/Components/SupplyLandInactivityModal";
import resetServerSideInactivity from "@SupplyLand/Handlers/ResetServerSideInactivity";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    state,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    signOut,
    resetServerSideInactivity,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerContainer?: InjectableCss;
    subContainer?: InjectableCss;
    promoBanner?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
            padding: 0px !important;
            margin: 0;
            max-width: 100% !important;
            border-bottom: 5px solid black;
            z-index: 1;
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.contrast")};
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    subContainer: {
        css: css`
            max-width: 1110px;
            margin: 0 auto;
            display: flex;
            font-weight: bold;
            flex-grow: 1;
        `,
    },
    promoBanner: {
        css: css`
            background-color: ${getColor("primary.main")};
            min-height: 20px;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    state,
    session,
    toaster,
    history,
    signOut,
    myAccountPageUrl,
    dismissRfqUpdates,
    resetServerSideInactivity,
}: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const pollingInterval = 1000 * 5 * 1; // 3 seconds
    const lastActivityTimeCookieName = "slLastActivityTime";

    useEffect(() => {
        if ((session.isAuthenticated && !session.isGuest) || (session.isAuthenticated && session.isGuest)) {
            const interval = setInterval(() => {
                const inactiveMinutes = calculateInactiveMinutes();
                switch (inactiveMinutes) {
                    case 11:
                    case 12:
                    case 13:
                        if (isModalOpen === false) {
                            setIsModalOpen(true);
                        }
                        break;
                    case 14:
                        signOut();
                        break;
                    default:
                        if (isModalOpen === true) {
                            setIsModalOpen(false);
                        }
                        break;
                }
            }, pollingInterval);

            return () => {
                clearInterval(interval);
            };
        }
    }, []);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const updateLastActivityTime = () => {
        if (typeof window !== undefined) {
            try {
                // console.log("updateLastActivityTime - localStorage.setItem()");
                localStorage.setItem(lastActivityTimeCookieName, new Date().toISOString());
            } catch (e) {
                // this try/catch prevents unhandled promise rejection warnings from showing up in the console and logs
            }
        }
    };

    updateLastActivityTime();

    const calculateInactiveMinutes = () => {
        const lastActivityTime = localStorage.getItem(lastActivityTimeCookieName);
        if (lastActivityTime === null) {
            updateLastActivityTime();
            return 0;
        }
        const lastActivityDateTime = new Date(lastActivityTime!);
        const currentDateTime = new Date();
        const span = currentDateTime.valueOf() - lastActivityDateTime.valueOf();
        const elapsedMinutes = Math.floor(span / (1000 * 60 * 1));
        // console.log(`inactiveMinutes: ${elapsedMinutes} (span: ${span}ms)`);
        return elapsedMinutes;
    };

    const onCloseHandler = () => {
        setIsModalOpen(false);
    };

    const onStaySignedInHandler = () => {
        const localSession = resetServerSideInactivity();
        setIsModalOpen(false);
    };

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <StyledWrapper {...styles.promoBanner}>
                        <Zone contentId={id} zoneName="PromoBanner" />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="SecondaryNavigation" />
                    <StyledWrapper {...styles.container}>
                        <StyledWrapper {...styles.subContainer}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
                <SupplyLandInactivityModal isModalOpen={isModalOpen} onStaySignedInHandler={onStaySignedInHandler} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
